import { DOCUMENT, inject, Injectable, signal } from "@angular/core";
import { Maintenance } from "../interfaces/maintenance";
import { ERoles } from "../enums/eroles";
import { MitpManagerService } from "./mitp-manager.service";

/**
 * Whether the platform is in maintenance, as last reported by the backend: by the `/status`
 * probe of `ConnectivityService`, or by a 503 caught in `HttpInterceptorService`.
 *
 * Everyone but the supreme admins gets the maintenance screen; they keep working, under a banner.
 * The page reloads once the maintenance ends, so that nothing half-loaded during it stays on screen.
 */
@Injectable({
	providedIn: "root",
})
export class MaintenanceService {
	private mitpManager = inject(MitpManagerService);
	private document = inject(DOCUMENT);

	readonly state = signal<Maintenance | null>(null);
	/** The screen was set aside so that a supreme admin can log in. */
	private dismissed = signal(false);

	update(state: Maintenance | null): void {
		const wasActive = this.state() !== null;
		if (state === null) {
			this.state.set(null);
			if (wasActive) {
				this.reload();
			}
			return;
		}
		// A refused call means the user is not one who may get through: the screen comes back.
		if (!wasActive || this.dismissed()) {
			this.dismissed.set(false);
		}
		this.state.set(state);
	}

	/**
	 * Sets the screen aside, to reach the login page: `mitp.adminAccess()` in the browser console.
	 * Any call refused afterwards brings it back.
	 */
	dismiss(): string {
		if (this.state() === null) {
			return "No maintenance in progress";
		}
		this.dismissed.set(true);
		return "Maintenance screen hidden: log in with a supreme admin account";
	}

	showScreen(): boolean {
		return this.state() !== null && !this.dismissed() && !this.isSupremeAdmin();
	}

	showAdminBanner(): boolean {
		return this.state() !== null && this.isSupremeAdmin();
	}

	protected reload(): void {
		this.document.defaultView?.location.reload();
	}

	private isSupremeAdmin(): boolean {
		return this.mitpManager.isGranted(ERoles.ROLE_SUPREME_ADMIN);
	}
}
